import { useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api-client';
import { useAuth } from '../lib/auth-context';
import { cn } from '../lib/cn';

/**
 * Admin Account CRUD — superadmin only.
 * Full spec: docs/DESIGN-admin-platform.md §3 (role matrix) + API-INVENTORY.md §11.5.
 * Endpoints live: GET /admins, POST /admins, PATCH /admins/{id}, POST /admins/{id}/reset-mfa.
 */

type AdminSummary = {
  adminId: string | null;
  email: string | null;
  emailVerified: boolean;
  role: string | null;
  status: string | null;
  username: string;
  cognitoStatus: string;
  enabled: boolean;
  createdAt: string | null;
  lastModifiedAt: string | null;
};

type ListResponse = { admins: AdminSummary[] };
type CreateResponse = {
  admin: { adminId: string; email: string; role: string; cognitoStatus: string; createdAt: string };
  invitationSent: boolean;
};

type RowAction =
  | { kind: 'edit'; admin: AdminSummary }
  | { kind: 'reset-mfa'; admin: AdminSummary };

const ROLES = ['superadmin', 'admin', 'support-lead', 'csr', 'read-only'] as const;

export function AdminsPage() {
  const queryClient = useQueryClient();
  const auth = useAuth();
  const currentAdminId = auth.kind === 'signed-in' ? auth.claims.adminId : null;
  const isSuperadmin = auth.hasRole('superadmin');

  const [showInvite, setShowInvite] = useState(false);
  const [action, setAction] = useState<RowAction | null>(null);

  const listQuery = useQuery({
    queryKey: ['admins'],
    queryFn: () => api.get<ListResponse>('/admins'),
  });

  const createMutation = useMutation({
    mutationFn: (body: { email: string; role: string; firstName?: string; lastName?: string; reason: string }) =>
      api.post<CreateResponse>('/admins', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admins'] });
      setShowInvite(false);
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-lg font-semibold">Admin Accounts</h1>
          <p className="text-sm text-muted">
            {listQuery.data ? `${listQuery.data.admins.length} admins` : 'Loading…'}
          </p>
        </div>
        {isSuperadmin && (
          <button
            onClick={() => setShowInvite(true)}
            className="bg-accent text-accent-fg text-sm font-medium px-3 py-1.5 rounded hover:opacity-95"
          >
            Invite admin
          </button>
        )}
      </div>

      {showInvite && (
        <InviteForm
          onSubmit={(body) => createMutation.mutate(body)}
          onCancel={() => {
            createMutation.reset();
            setShowInvite(false);
          }}
          error={createMutation.error}
          pending={createMutation.isPending}
        />
      )}

      {listQuery.isLoading && <LoadingBlock />}
      {listQuery.isError && <ErrorBlock error={listQuery.error} />}
      {listQuery.data && (
        <AdminsTable
          admins={listQuery.data.admins}
          currentAdminId={currentAdminId}
          canManage={isSuperadmin}
          onEdit={(admin) => setAction({ kind: 'edit', admin })}
          onResetMfa={(admin) => setAction({ kind: 'reset-mfa', admin })}
        />
      )}

      {action?.kind === 'edit' && (
        <EditAdminDialog
          admin={action.admin}
          onClose={() => setAction(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['admins'] });
            setAction(null);
          }}
        />
      )}
      {action?.kind === 'reset-mfa' && (
        <ResetMfaDialog
          admin={action.admin}
          onClose={() => setAction(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['admins'] });
            setAction(null);
          }}
        />
      )}
    </div>
  );
}

function InviteForm({
  onSubmit,
  onCancel,
  error,
  pending,
}: {
  onSubmit: (body: { email: string; role: string; firstName?: string; lastName?: string; reason: string }) => void;
  onCancel: () => void;
  error: unknown;
  pending: boolean;
}) {
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    onSubmit({
      email: String(fd.get('email')),
      role: String(fd.get('role')),
      firstName: String(fd.get('firstName') || '') || undefined,
      lastName: String(fd.get('lastName') || '') || undefined,
      reason: String(fd.get('reason')),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="panel p-4 space-y-3">
      <div className="text-sm font-medium">Invite new admin</div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Email" name="email" type="email" required autoFocus />
        <Field label="Role" name="role" required>
          <select name="role" required className="w-full px-2 py-1.5 text-sm bg-bg border border-border rounded focus:border-accent outline-none">
            <option value="">Choose role</option>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="First name" name="firstName" type="text" />
        <Field label="Last name" name="lastName" type="text" />
      </div>
      <Field label="Reason (required, ≥10 chars — audited)" name="reason" required minLength={10}>
        <textarea
          name="reason"
          required
          minLength={10}
          rows={2}
          placeholder="e.g. Onboarding Santi as admin per 2026-04 sales partnership"
          className="w-full px-2 py-1.5 text-sm bg-bg border border-border rounded focus:border-accent outline-none resize-none"
        />
      </Field>

      {error instanceof ApiError && (
        <div className="text-xs text-danger bg-danger/10 border border-danger/30 rounded px-3 py-2">
          {error.status} · {error.code} · {error.message}
        </div>
      )}

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={pending}
          className="text-sm px-3 py-1.5 rounded border border-border hover:bg-bg"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={pending}
          className="bg-accent text-accent-fg text-sm font-medium px-3 py-1.5 rounded hover:opacity-95 disabled:opacity-60"
        >
          {pending ? 'Inviting…' : 'Send invitation'}
        </button>
      </div>
    </form>
  );
}

/**
 * Edit dialog — PATCH /admins/{adminId}. Role swap and/or status change.
 * Sends only changed fields so the server audits a minimal diff.
 */
function EditAdminDialog({
  admin,
  onClose,
  onSaved,
}: {
  admin: AdminSummary;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [role, setRole] = useState(admin.role ?? '');
  const [status, setStatus] = useState(admin.status ?? 'active');

  type PatchBody = { role?: string; status?: string; reason: string };
  type PatchResponse = {
    admin: { adminId: string; email: string; role: string; status: string };
    changes: { role: { from: string; to: string } | null; status: { from: string; to: string } | null };
  };

  const patch = useMutation({
    mutationFn: (body: PatchBody) =>
      api.patch<PatchResponse>(`/admins/${admin.adminId}`, body),
    onSuccess: onSaved,
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const reason = String(fd.get('reason'));
    const body: PatchBody = { reason };
    if (role && role !== admin.role) body.role = role;
    if (status && status !== admin.status) body.status = status;
    if (!body.role && !body.status) return;
    patch.mutate(body);
  };

  const dirty = (role && role !== admin.role) || (status && status !== admin.status);

  return (
    <Modal onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <div className="text-sm font-medium">Edit admin</div>
          <div className="text-xs text-muted mt-0.5 truncate">{admin.email ?? admin.username}</div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Role" name="role">
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-2 py-1.5 text-sm bg-bg border border-border rounded focus:border-accent outline-none"
            >
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Status" name="status">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-2 py-1.5 text-sm bg-bg border border-border rounded focus:border-accent outline-none"
            >
              <option value="active">active</option>
              <option value="suspended">suspended</option>
            </select>
          </Field>
        </div>

        <Field label="Reason (required, ≥10 chars — audited)" name="reason" required minLength={10}>
          <textarea
            name="reason"
            required
            minLength={10}
            rows={2}
            placeholder="e.g. Promoting Santi to support-lead after 30-day probation"
            className="w-full px-2 py-1.5 text-sm bg-bg border border-border rounded focus:border-accent outline-none resize-none"
          />
        </Field>

        {patch.error instanceof ApiError && (
          <div className="text-xs text-danger bg-danger/10 border border-danger/30 rounded px-3 py-2">
            {patch.error.status} · {patch.error.code} · {patch.error.message}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={patch.isPending}
            className="text-sm px-3 py-1.5 rounded border border-border hover:bg-bg"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={patch.isPending || !dirty}
            className="bg-accent text-accent-fg text-sm font-medium px-3 py-1.5 rounded hover:opacity-95 disabled:opacity-60"
          >
            {patch.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Reset MFA dialog — POST /admins/{adminId}/reset-mfa.
 * Disables TOTP preference + revokes sessions. Next login forces MFA_SETUP.
 */
function ResetMfaDialog({
  admin,
  onClose,
  onSaved,
}: {
  admin: AdminSummary;
  onClose: () => void;
  onSaved: () => void;
}) {
  type ResetResponse = { adminId: string; email: string; mfaReset: boolean; sessionsRevoked: boolean };

  const reset = useMutation({
    mutationFn: (body: { reason: string; signOutSessions?: boolean }) =>
      api.post<ResetResponse>(`/admins/${admin.adminId}/reset-mfa`, body),
    onSuccess: onSaved,
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    reset.mutate({
      reason: String(fd.get('reason')),
      signOutSessions: fd.get('signOutSessions') === 'on',
    });
  };

  return (
    <Modal onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <div className="text-sm font-medium">Reset MFA</div>
          <div className="text-xs text-muted mt-0.5 truncate">{admin.email ?? admin.username}</div>
        </div>

        <div className="text-xs text-muted bg-bg border border-border rounded px-3 py-2 space-y-1">
          <div>This will disable the admin's TOTP enrollment. On their next login they will be forced through MFA setup again with a fresh authenticator secret.</div>
          <div>Use for lost-device / recovery scenarios. The admin keeps their existing password.</div>
        </div>

        <label className="flex items-center gap-2 text-xs text-fg">
          <input
            type="checkbox"
            name="signOutSessions"
            defaultChecked
            className="rounded border-border"
          />
          Also revoke active sessions (recommended)
        </label>

        <Field label="Reason (required, ≥10 chars — audited)" name="reason" required minLength={10}>
          <textarea
            name="reason"
            required
            minLength={10}
            rows={2}
            placeholder="e.g. Lost phone — verified identity over support call 2026-04-22"
            className="w-full px-2 py-1.5 text-sm bg-bg border border-border rounded focus:border-accent outline-none resize-none"
          />
        </Field>

        {reset.error instanceof ApiError && (
          <div className="text-xs text-danger bg-danger/10 border border-danger/30 rounded px-3 py-2">
            {reset.error.status} · {reset.error.code} · {reset.error.message}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={reset.isPending}
            className="text-sm px-3 py-1.5 rounded border border-border hover:bg-bg"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={reset.isPending}
            className="bg-danger text-white text-sm font-medium px-3 py-1.5 rounded hover:opacity-95 disabled:opacity-60"
          >
            {reset.isPending ? 'Resetting…' : 'Reset MFA'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="panel p-4 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  name,
  type,
  required,
  autoFocus,
  minLength,
  children,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  autoFocus?: boolean;
  minLength?: number;
  children?: React.ReactNode;
}) {
  return (
    <label className="block space-y-1 col-span-full md:col-span-1" data-span={children ? 'full' : undefined}>
      <span className="text-xs text-muted">{label}</span>
      {children ?? (
        <input
          name={name}
          type={type ?? 'text'}
          required={required}
          autoFocus={autoFocus}
          minLength={minLength}
          className="w-full px-2 py-1.5 text-sm bg-bg border border-border rounded focus:border-accent outline-none"
        />
      )}
    </label>
  );
}

function AdminsTable({
  admins,
  currentAdminId,
  canManage,
  onEdit,
  onResetMfa,
}: {
  admins: AdminSummary[];
  currentAdminId: string | null;
  canManage: boolean;
  onEdit: (admin: AdminSummary) => void;
  onResetMfa: (admin: AdminSummary) => void;
}) {
  if (admins.length === 0) {
    return <div className="panel p-8 text-center text-sm text-muted">No admins (that's weird)</div>;
  }
  return (
    <div className="panel overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-bg text-xs text-muted uppercase tracking-wide">
          <tr>
            <Th>Email</Th>
            <Th>Role</Th>
            <Th>Cognito Status</Th>
            <Th>App Status</Th>
            <Th>Created</Th>
            <Th>adminId</Th>
            {canManage && <Th>Actions</Th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {admins.map((a) => {
            const isSelf = a.adminId != null && a.adminId === currentAdminId;
            return (
              <tr key={a.adminId ?? a.username} className="hover:bg-bg/60">
                <Td>
                  <div className="font-medium text-fg">
                    {a.email ?? a.username}
                    {isSelf && <span className="ml-2 text-xs text-muted">(you)</span>}
                  </div>
                  {!a.emailVerified && <div className="text-xs text-muted">email unverified</div>}
                </Td>
                <Td><RoleBadge role={a.role} /></Td>
                <Td className="text-xs"><code>{a.cognitoStatus}</code></Td>
                <Td><Badge value={a.status ?? '—'} /></Td>
                <Td className="text-xs text-muted">{formatDate(a.createdAt)}</Td>
                <Td className="text-xs font-mono text-muted">{a.adminId ?? '—'}</Td>
                {canManage && (
                  <Td>
                    <RowActions
                      admin={a}
                      isSelf={isSelf}
                      onEdit={() => onEdit(a)}
                      onResetMfa={() => onResetMfa(a)}
                    />
                  </Td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RowActions({
  admin,
  isSelf,
  onEdit,
  onResetMfa,
}: {
  admin: AdminSummary;
  isSelf: boolean;
  onEdit: () => void;
  onResetMfa: () => void;
}) {
  // Reset MFA only makes sense once the admin is past FORCE_CHANGE_PASSWORD — before that,
  // they haven't enrolled TOTP yet, so resetting it is a no-op.
  const mfaResettable = admin.cognitoStatus === 'CONFIRMED';
  // Server enforces self-modification guard for PATCH too; mirror it client-side for clarity.
  return (
    <div className="flex gap-2">
      <button
        onClick={onEdit}
        disabled={isSelf || !admin.adminId}
        title={isSelf ? 'Cannot edit your own account' : 'Change role or status'}
        className="text-xs px-2 py-1 rounded border border-border hover:bg-bg disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Edit
      </button>
      <button
        onClick={onResetMfa}
        disabled={isSelf || !admin.adminId || !mfaResettable}
        title={
          isSelf
            ? 'Cannot reset your own MFA'
            : !mfaResettable
            ? `Admin has not enrolled TOTP yet (status: ${admin.cognitoStatus})`
            : 'Force MFA re-enrollment on next login'
        }
        className="text-xs px-2 py-1 rounded border border-border hover:bg-bg disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Reset MFA
      </button>
    </div>
  );
}

function RoleBadge({ role }: { role: string | null }) {
  const tone =
    role === 'superadmin'
      ? 'bg-accent/20 text-accent'
      : role === 'admin'
      ? 'bg-accent/15 text-accent'
      : role === 'support-lead'
      ? 'bg-accent/10 text-accent'
      : role === 'csr'
      ? 'bg-border/60 text-fg'
      : 'bg-muted/10 text-muted';
  return <span className={cn('inline-block text-xs px-1.5 py-0.5 rounded', tone)}>{role ?? '—'}</span>;
}

function Badge({ value }: { value: string }) {
  return <span className="inline-block text-xs px-1.5 py-0.5 rounded bg-border/60 text-fg">{value}</span>;
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-left px-3 py-2 font-medium">{children}</th>;
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-3 py-2 align-top', className)}>{children}</td>;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function LoadingBlock() {
  return <div className="panel p-8 text-center text-sm text-muted">Loading admins…</div>;
}

function ErrorBlock({ error }: { error: unknown }) {
  const msg =
    error instanceof ApiError ? `${error.status} · ${error.code} · ${error.message}` : String(error);
  return (
    <div className="panel p-4 text-sm text-danger bg-danger/5 border-danger/30">
      <div className="font-medium">Failed to load admins</div>
      <div className="text-xs mt-1 font-mono">{msg}</div>
    </div>
  );
}
